import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Package, Calendar, User, Truck, Mail, MapPin, Send, Bell, RefreshCw, UserX, Building, Phone, Mail as MailIcon, Clock, AlertCircle } from 'lucide-react'
import { deliveryApi } from '../services/api'
import { showError } from '../utils/showError'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'

const DeliveryDetail = () => {
  const { deliveryId } = useParams()
  const navigate = useNavigate()
  const [delivery, setDelivery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    loadDeliveryDetails()
  }, [deliveryId])

  const loadDeliveryDetails = async () => {
    try {
      setLoading(true)
      const response = await deliveryApi.get(deliveryId)
      console.log('Delivery details API response:', response.data)
      setDelivery(response.data)
    } catch (error) {
      console.error('Error loading delivery details:', error)
      setDelivery(null)
    } finally {
      setLoading(false)
    }
  }


  const handleDelete = async () => {
    try {
      setActionLoading(true)
      await deliveryApi.delete(deliveryId, true) // force delete
      navigate('/deliveries')
    } catch (error) {
      console.error('Error deleting delivery:', error)
      showError(error)
    } finally {
      setActionLoading(false)
      setShowDeleteModal(false)
    }
  }

  const handleMarkDelivered = async () => {
    try {
      setActionLoading(true)
      await deliveryApi.markDelivered(deliveryId)
      await loadDeliveryDetails() // Reload to get updated data
    } catch (error) {
      console.error('Error marking as delivered:', error)
      showError(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleMarkCollected = async () => {
    try {
      setActionLoading(true)
      await deliveryApi.markCollected(deliveryId)
      await loadDeliveryDetails() // Reload to get updated data
    } catch (error) {
      console.error('Error marking as collected:', error)
      showError(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleTransfer = async (transferData) => {
    try {
      setActionLoading(true)
      await deliveryApi.transfer(deliveryId, transferData)
      await loadDeliveryDetails()
      setShowTransferModal(false)
    } catch (error) {
      console.error('Error transferring delivery:', error)
      showError(error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleResendNotification = async (notificationData) => {
    try {
      setActionLoading(true)
      await deliveryApi.resendNotification(deliveryId, notificationData)
      setShowNotificationModal(false)
      alert('Notification sent successfully!')
    } catch (error) {
      console.error('Error resending notification:', error)
      showError(error)
    } finally {
      setActionLoading(false)
    }
  }


  const handleSendReminder = async (reminderData) => {
    try {
      setActionLoading(true)
      await deliveryApi.sendReminder(deliveryId, reminderData)
      setShowReminderModal(false)
      alert('Reminder sent successfully!')
    } catch (error) {
      console.error('Error sending reminder:', error)
      showError(error)
    } finally {
      setActionLoading(false)
    }
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 1: return <Package className="w-5 h-5" />
      case 2: return <Mail className="w-5 h-5" />
      default: return <Truck className="w-5 h-5" />
    }
  }

  const getDeliveryTypeText = (type) => {
    switch (type) {
      case 1: return 'Package'
      case 2: return 'Mail'
      default: return 'Unknown'
    }
  }

  const getDeliveryTypeColor = (type) => {
    switch (type) {
      case 1: return 'bg-blue-100 text-blue-800'
      case 2: return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (delivery) => {
    if (!delivery) return 'bg-gray-100 text-gray-800'
    if (delivery.isCollected) return 'bg-blue-100 text-blue-800'
    if (delivery.isDelivered) return 'bg-green-100 text-green-800'
    return 'bg-yellow-100 text-yellow-800' // pending
  }

  const getStatusText = (delivery) => {
    if (!delivery) return 'Unknown'
    if (delivery.isCollected) return 'Collected'
    if (delivery.isDelivered) return 'Delivered'
    return 'Pending'
  }

  const isOverdue = (delivery) => {
    if (!delivery || delivery.isCollected || !delivery.isDelivered) return false
    const deliveryDate = new Date(delivery.deliveryDate)
    const now = new Date()
    const daysDiff = (now - deliveryDate) / (1000 * 60 * 60 * 24)
    return daysDiff > 7 // Consider overdue after 7 days
  }

  const formatProfilePictureUrl = (url) => {
    if (!url) return null
    if (url.startsWith('http')) return url
    return `https://${url}`
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!delivery) {
    return (
      <div className="text-center py-12">
        <Package className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Delivery not found</h3>
        <p className="mt-1 text-sm text-gray-500">The delivery you're looking for doesn't exist.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <button
              onClick={() => navigate('/deliveries')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            Delivery Details
          </div>
        }
        action={
          <div className="flex space-x-3">
            <button
              onClick={() => navigate(`/deliveries/${deliveryId}/edit`)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={!delivery?.canDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </button>
          </div>
        }
      />

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg ${getDeliveryTypeColor(delivery.deliveryType)}`}>
                {getDeliveryTypeIcon(delivery.deliveryType)}
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {delivery.serialNumber || `Delivery #${delivery.deliveryId}`}
                </h3>
                <p className="text-sm text-gray-500">{getDeliveryTypeText(delivery.deliveryType)}</p>
                {delivery.isAIGenerated && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 mt-1">
                    AI Generated
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isOverdue(delivery) && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Overdue
                </span>
              )}
              <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(delivery)}`}>
                {getStatusText(delivery)}
              </span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Recipient</dt>
              <dd className="mt-1 flex items-center">
                {delivery.recipientProfile?.profilePictureUrl ? (
                  <img
                    src={formatProfilePictureUrl(delivery.recipientProfile.profilePictureUrl)}
                    alt={delivery.recipientName}
                    className="w-8 h-8 rounded-full mr-3"
                  />
                ) : (
                  <User className="w-4 h-4 mr-2 text-gray-400" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {delivery.recipientName || 'Unknown Recipient'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {delivery.recipientEmail || 'No email'}
                  </div>
                  {delivery.recipientPhone && (
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <Phone className="w-3 h-3 mr-1" />
                      {delivery.recipientPhone}
                    </div>
                  )}
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Building & Unit</dt>
              <dd className="mt-1 flex items-center">
                <Building className="w-4 h-4 mr-2 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {delivery.buildingName || 'Unknown Building'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {delivery.recipientUnitNumber ? `Unit ${delivery.recipientUnitNumber}` : 'No Unit'}
                    {delivery.recipientFloorNumber && ` - Floor ${delivery.recipientFloorNumber}`}
                  </div>
                  {delivery.buildingUnitName && (
                    <div className="text-sm text-gray-500">{delivery.buildingUnitName}</div>
                  )}
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Sender</dt>
              <dd className="mt-1">
                <div className="text-sm font-medium text-gray-900">
                  {delivery.senderName || 'Unknown Sender'}
                </div>
                {delivery.senderAddress && (
                  <div className="text-sm text-gray-500">{delivery.senderAddress}</div>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created Date</dt>
              <dd className="mt-1 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-sm text-gray-900">{formatDate(delivery.createDateTimeUtc)}</span>
              </dd>
            </div>
            {delivery.deliveryDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Delivery Date</dt>
                <dd className="mt-1 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(delivery.deliveryDate)}</span>
                </dd>
              </div>
            )}
            {delivery.collectedDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Collected Date</dt>
                <dd className="mt-1 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(delivery.collectedDate)}</span>
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Days Waiting</dt>
              <dd className="mt-1 flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                <span className="text-sm text-gray-900">{delivery.daysWaiting || 0} days</span>
              </dd>
            </div>
            {delivery.serialNumber && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Serial Number</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{delivery.serialNumber}</dd>
              </div>
            )}
          </dl>

          {delivery.recipientAddress && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Recipient Address</dt>
              <dd className="mt-1 text-sm text-gray-900">{delivery.recipientAddress}</dd>
            </div>
          )}

          {delivery.buildingAddress && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Building Address</dt>
              <dd className="mt-1 text-sm text-gray-900">{delivery.buildingAddress}</dd>
            </div>
          )}

          {delivery.notificationMethods && delivery.notificationMethods.length > 0 && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Notification Methods</dt>
              <dd className="mt-1 flex space-x-2">
                {delivery.notificationMethods.map((method, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                    {method === 1 && <><MailIcon className="w-3 h-3 mr-1" />Email</>}
                    {method === 2 && <><Phone className="w-3 h-3 mr-1" />SMS</>}
                    {method === 3 && <><Bell className="w-3 h-3 mr-1" />Push</>}
                  </span>
                ))}
              </dd>
            </div>
          )}

          {delivery.attachments && delivery.attachments.length > 0 && (
            <div className="mt-6">
              <dt className="text-sm font-medium text-gray-500">Attachments</dt>
              <dd className="mt-1">
                <div className="flex flex-wrap gap-2">
                  {delivery.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                    >
                      📎 {attachment.fileName || `Attachment ${index + 1}`}
                    </a>
                  ))}
                </div>
              </dd>
            </div>
          )}
        </div>
      </div>

      {/* Status Update Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Actions</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Status Actions */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Status Updates</h4>
              <div className="space-y-2">
                {!delivery.isDelivered && delivery.canMarkAsDelivered && (
                  <button
                    onClick={handleMarkDelivered}
                    disabled={actionLoading}
                    className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Mark as Delivered'}
                  </button>
                )}
                {delivery.isDelivered && !delivery.isCollected && delivery.canForceCollection && (
                  <button
                    onClick={handleMarkCollected}
                    disabled={actionLoading}
                    className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Mark as Collected'}
                  </button>
                )}
              </div>
            </div>

            {/* Communication Actions */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Communications</h4>
              <div className="space-y-2">
                <button
                  onClick={() => setShowNotificationModal(true)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Resend Notification
                </button>
                <button
                  onClick={() => setShowReminderModal(true)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Bell className="mr-2 h-4 w-4" />
                  Send Reminder
                </button>
              </div>
            </div>

            {/* Management Actions */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Management</h4>
              <div className="space-y-2">
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserX className="mr-2 h-4 w-4" />
                  Transfer Delivery
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Modals */}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">Delete Delivery</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this delivery record? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && (
        <TransferModal
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransfer}
          loading={actionLoading}
        />
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <NotificationModal
          onClose={() => setShowNotificationModal(false)}
          onSend={handleResendNotification}
          loading={actionLoading}
        />
      )}


      {/* Reminder Modal */}
      {showReminderModal && (
        <ReminderModal
          onClose={() => setShowReminderModal(false)}
          onSend={handleSendReminder}
          loading={actionLoading}
        />
      )}

    </div>
  )
}

// Modal Components
const TransferModal = ({ onClose, onTransfer, loading }) => {
  const [formData, setFormData] = useState({
    newRecipientUserId: '',
    newBuildingUnitId: ''
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onTransfer(formData)
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Delivery</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">New Recipient User ID</label>
              <input
                type="text"
                value={formData.newRecipientUserId}
                onChange={(e) => setFormData({ ...formData, newRecipientUserId: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Building Unit ID</label>
              <input
                type="number"
                value={formData.newBuildingUnitId}
                onChange={(e) => setFormData({ ...formData, newBuildingUnitId: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Transferring...' : 'Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const NotificationModal = ({ onClose, onSend, loading }) => {
  const [notificationMethods, setNotificationMethods] = useState([1]) // Default to email

  const handleSubmit = (e) => {
    e.preventDefault()
    onSend({ notificationMethods })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Resend Notification</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notification Methods</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={notificationMethods.includes(1)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNotificationMethods([...notificationMethods, 1])
                      } else {
                        setNotificationMethods(notificationMethods.filter(m => m !== 1))
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={notificationMethods.includes(2)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNotificationMethods([...notificationMethods, 2])
                      } else {
                        setNotificationMethods(notificationMethods.filter(m => m !== 2))
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">SMS</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={notificationMethods.includes(3)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNotificationMethods([...notificationMethods, 3])
                      } else {
                        setNotificationMethods(notificationMethods.filter(m => m !== 3))
                      }
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Push Notification</span>
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || notificationMethods.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Notification'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


const ReminderModal = ({ onClose, onSend, loading }) => {
  const [customMessage, setCustomMessage] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSend({ customMessage })
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Send Collection Reminder</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Custom Message</label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Please collect your package from the front desk..."
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Reminder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


export default DeliveryDetail